import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/api";
import {
  Eye,
  Search,
  RefreshCw,
  LifeBuoy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface SupportTicket {
  id: number;
  userId: number;
  subject: string;
  status: string;
  priority: string;
  createdAt: string;
}

export function SupportTicketsTab() {
  const [, setLocation] = useLocation();
  const { data: tickets, isLoading } = useQuery<SupportTicket[]>({
    queryKey: ["admin-support-tickets"],
    queryFn: async () => {
      const response = await apiRequest("/api/admin/support-tickets");
      return response as SupportTicket[];
    },
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");

  const handleManageTicket = (ticketId: number) => {
    setLocation(`/admin/support/${ticketId}`);
  };

  // Filter tickets based on search query and filters
  const filteredTickets = tickets
    ? tickets.filter((ticket: SupportTicket) => {
        // Search filter
        const matchesSearch = [
          ticket.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false,
          ticket.id?.toString().includes(searchQuery) ?? false,
          ticket.userId?.toString().includes(searchQuery) ?? false
        ].some(Boolean);

        // Status filter
        const matchesStatus =
          statusFilter === "all" || ticket.status === statusFilter;

        // Priority filter
        const matchesPriority =
          priorityFilter === "all" || ticket.priority === priorityFilter;

        return matchesSearch && matchesStatus && matchesPriority;
      })
    : [];

  if (isLoading) {
    return <div className="text-center py-8">Loading support tickets...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold mb-4">Support Tickets</h2>
        <Button
          onClick={() => setLocation("/admin/support")}
          className="flex items-center gap-2"
        >
          <LifeBuoy className="w-4 h-4" />
          Manage All Tickets
        </Button>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by subject, ID or user ID..."
              className="pl-9 w-full rounded-md border border-input bg-slate-800 py-2 text-sm text-white ring-offset-background placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-2">
          <select
            className="rounded-md border border-input bg-slate-800 px-3 py-2 text-sm text-white ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="closed">Closed</option>
          </select>

          <select
            className="rounded-md border border-input bg-slate-800 px-3 py-2 text-sm text-white ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
          >
            <option value="all">All Priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSearchQuery("");
              setStatusFilter("all");
              setPriorityFilter("all");
            }}
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Reset
          </Button>
        </div>
      </div>

      {!tickets || tickets.length === 0 ? (
        <div className="text-center py-8 border rounded-lg bg-muted/50">
          No support tickets found
        </div>
      ) : filteredTickets.length === 0 ? (
        <div className="text-center py-8 border rounded-lg bg-muted/50">
          No tickets match your search criteria
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-2">ID</th>
                <th className="text-left p-2">User</th>
                <th className="text-left p-2">Subject</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Priority</th>
                <th className="text-left p-2">Created</th>
                <th className="text-left p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTickets.map((ticket) => (
                <tr key={ticket.id} className="border-t hover:bg-muted/50">
                  <td className="p-2">{ticket.id}</td>
                  <td className="p-2">{ticket.userId}</td>
                  <td className="p-2 max-w-[200px] truncate">
                    {ticket.subject}
                  </td>
                  <td className="p-2">
                    <Badge
                      className={
                        ticket.status === "open"
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                          : ticket.status === "in_progress"
                            ? "bg-blue-600 hover:bg-blue-700 text-white"
                            : "bg-green-600 hover:bg-green-700 text-white"
                      }
                    >
                      {ticket.status.replace("_", " ")}
                    </Badge>
                  </td>
                  <td className="p-2">
                    <Badge
                      className={
                        ticket.priority === "high"
                          ? "bg-red-600 hover:bg-red-700 text-white"
                          : ticket.priority === "medium"
                            ? "bg-yellow-600 hover:bg-yellow-700 text-white"
                            : "bg-green-600 hover:bg-green-700 text-white"
                      }
                    >
                      {ticket.priority}
                    </Badge>
                  </td>
                  <td className="p-2">
                    {new Date(ticket.createdAt).toLocaleDateString()}
                  </td>
                  <td className="p-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleManageTicket(ticket.id)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Manage
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}